/** The full family view of a company. */ export const companyFamilyHref = (id)=>`/registries/companies/${encodeURIComponent(id)}/family`;
/**
 * "dba Kentro · trading as Kentro Digital · formerly SRA International (2019)":
 * names a company trades under first, then its old names with the year they
 * changed when known. Empty when it has none.
 */ export function aliasLine(aliases) {
    const year = (a)=>{
        const t = a.endDate ?? a.effectiveDate;
        return t ? ` (${t.toDate().getUTCFullYear()})` : '';
    };
    const parts = aliases.map((a)=>{
        switch(a.aliasType){
            case 'dba':
                return `dba ${a.name}`;
            case 'trading_as':
                return `trading as ${a.name}`;
            default:
                return `formerly ${a.name}${year(a)}`;
        }
    });
    return parts.filter((p, i)=>parts.indexOf(p) === i).join(' · ');
}
const VIA = {
    subsidiary: 'subsidiary of',
    acquisition: 'acquired by',
    merger: 'merged into',
    jv_member: 'JV of'
};
/** "Cognosante MVH → Cognosante → Accenture Federal Services → Accenture plc". */ export function umbrellaPath(companyName, u) {
    return [
        companyName,
        ...u.path.map((h)=>h.company?.name ?? '')
    ].filter(Boolean).join(' → ');
}
/** How the company first sits under its umbrella, in words ("subsidiary of"). */ export function umbrellaVia(u) {
    return VIA[u.via] ?? 'part of';
}
/** The "To us" order: who we are to them first, then the strongest tie. */ export const ROLE_PRIORITY = [
    'OURS',
    'PARTNER',
    'COMPETITOR',
    'PRIME_WE_SUB_TO',
    'TEAMMATE',
    'OUR_SUB',
    'PART_OF'
];
export const ROLE_LABEL = {
    OURS: 'Ours',
    PARTNER: 'Partner',
    COMPETITOR: 'Competitor',
    PRIME_WE_SUB_TO: 'Prime we sub to',
    TEAMMATE: 'Teammate',
    OUR_SUB: 'Our sub',
    PART_OF: 'Part of',
    MENTOR_TO: 'Mentor to',
    PROTEGE_OF: 'Protégé of'
};
const rank = (k)=>{
    const i = ROLE_PRIORITY.indexOf(k);
    return i < 0 ? ROLE_PRIORITY.length : i;
};
const monthYear = (d)=>d.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC'
    });
/**
 * Every role a company has with us. A company can be a partner, a competitor on
 * one pursuit and our sub on a program all at once; each current role is its
 * own chip, in ROLE_PRIORITY order, with the pursuit or program it comes from.
 * Former roles are returned separately.
 */ export function companyRoles(rels) {
    const chip = (r)=>{
        const base = ROLE_LABEL[r.kind] ?? r.kind.toLowerCase().replace(/_/g, ' ');
        const ctx = r.context?.name;
        let label = base;
        if (ctx && r.kind !== 'PART_OF') label = `${base} on ${ctx}`;
        else if (ctx) label = `${base} ${ctx}`;
        else if (r.kind === 'PARTNER' && r.since) label = `${base} since ${monthYear(r.since.toDate())}`;
        return {
            kind: r.kind,
            label,
            contextId: r.context?.id,
            contextKind: r.context?.kind,
            qualifier: r.qualifier || undefined
        };
    };
    const sorted = [
        ...rels
    ].sort((a, b)=>rank(a.kind) - rank(b.kind));
    const dedupe = (cs)=>cs.filter((c, i)=>cs.findIndex((o)=>o.label === c.label) === i);
    return {
        current: dedupe(sorted.filter((r)=>!r.until).map(chip)),
        former: dedupe(sorted.filter((r)=>!!r.until).map(chip))
    };
}
/** The distinct current role kinds, for the list's "To us" chips. */ export function currentRoleKinds(rels) {
    const kinds = rels.filter((r)=>!r.until).map((r)=>r.kind);
    return [
        ...new Set(kinds)
    ].sort((a, b)=>rank(a) - rank(b));
}
/** The open designation of a kind, if any (one at most per kind). */ export function openDesignation(ds, kind = 'PARTNER') {
    return ds.find((d)=>d.kind === kind && !d.effectiveEnd);
}
